import { createAsyncThunk, createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import scrollIntoView from 'scroll-into-view-if-needed';
import { createPageViewRecord } from '../helpers';
import GaEventManager from 'services/ga/gaEventManager';
import { auctionsBidsActions } from 'state/auctionsBids.slice/auctionsBids.slice';
import { attachmentsActions } from 'state/attachments.slice/attachments.slice';
import { auctionsActions } from 'state/auctions.slice/auctions.slice';
import { batch } from 'react-redux';

const fetchAuctionDetails = createAsyncThunk(
  'auctionsPreview/fetchAuctionDetails',
  async (
    payload: {
      auction: { auction_id?: number; auction_number: string; description?: string };
      eventOrigin?: 'listing' | 'promoted' | undefined;
      scrollDelay?: number;
      scrollBehavior?: 'smooth' | 'auto';
    },
    { getState, dispatch, signal },
  ) => {
    const { auction } = payload;
    const state = getState() as RootState;
    const eventOrigin = payload.eventOrigin || state.auctionPreview.eventOrigin;
    const { scrollDelay = 0, scrollBehavior = 'auto' } = payload;

    const auctionRowEl = document.querySelector(`[data-id="${auction.auction_id}"]`);
    if (auctionRowEl && eventOrigin !== 'promoted') {
      const { top } = auctionRowEl.getBoundingClientRect();
      setTimeout(() => {
        scrollIntoView(auctionRowEl, {
          block: 'center',
          inline: 'center',
          behavior: scrollBehavior,
          scrollMode: top < 182 ? 'always' : 'if-needed', // element can be hidden behind sticky header
        });
      }, scrollDelay);
    }

    const { data }: AxiosResponse<AuctionsTypes.AuctionDetailsType> = await axios.get(
      `/api/buyers/auction/${auction.auction_number}/auction_details`,
      { signal },
    );

    const auctioned_subsets = data.auctioned_subsets?.map((subset) => {
      if (subset.item_type_system_name !== 'luxury_watches') {
        return subset;
      }
      const subsetData = { ...subset.data };
      const { luxury_watch_year_sold, luxury_watch_year_exact_or_circa } = subsetData;

      if (luxury_watch_year_sold && luxury_watch_year_sold.val) {
        const prefix = luxury_watch_year_exact_or_circa?.val === 'Circa' ? 'Circa ' : '';
        subsetData.luxury_watch_year_sold = {
          ...luxury_watch_year_sold,
          val: `${prefix}${luxury_watch_year_sold.val}`,
        };
        delete subsetData.luxury_watch_year_exact_or_circa;
      }

      return { ...subset, data: subsetData };
    });

    const details = {
      auction_number: auction.auction_number,
      description: auction.description,
      ...data,
      auctioned_subsets,
    };

    batch(() => {
      dispatch(attachmentsActions.fetchAuctionAttachments({ auction: details }));
      dispatch(auctionsBidsActions.fetchAuctionBids({ id: details.auction_id }));
      dispatch(auctionsBidsActions.fetchRapPrice(data));
      dispatch(auctionsActions.onOpenAuction({ auction_id: details.auction_id }));
    });

    createPageViewRecord(data.auction_id);

    GaEventManager.GA4SendActionOpen({
      actionedObject: state.router.action === 'POP' ? undefined : 'auctionPreview',
      fieldName: 'auctionId',
      fieldValue: auction.auction_number,
    });

    return { details };
  },
);

function isFetchingAuctions(action: AnyAction): boolean {
  return (
    action.type.includes('myAuctions/fetchMyAuctions') ||
    action.type.includes('auctions/fetchAuctions')
  );
}

export const initialState: AuctionPreviewTypes.AuctionPreviewStateType = {
  isOpen: false,
  isLoading: false,
  auction: null,
  currentRequestId: undefined,
  eventOrigin: undefined,
};

export const auctionPreviewSlice = createSlice({
  name: 'auctionPreview',
  initialState,
  reducers: {
    setAuctionDetails: (
      state,
      action: PayloadAction<{
        auction: { auction_id: number; auction_number: string; description?: string };
      }>,
    ) => {
      const { auction } = action.payload;

      return {
        ...state,
        auction,
      };
    },
    setAuctionDescription: (
      state,
      action: PayloadAction<{ auction_id?: number; description: string }>,
    ) => {
      if (!state.auction || state.auction.auction_id !== action.payload.auction_id) {
        return state;
      }

      return {
        ...state,
        auction: {
          ...state.auction,
          description: action.payload.description,
        },
      };
    },
    openAuctionPreview: (
      state,
      action: PayloadAction<{ auction: AuctionPreviewTypes.Auction }>,
    ) => {
      const { auction } = action.payload;
      return {
        ...state,
        auction,
        isOpen: true,
      };
    },
    closeAuctionPreview: (state) => {
      return {
        ...state,
        isLoading: false,
        isOpen: false,
        eventOrigin: undefined,
        auction: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAuctionDetails.pending, (state, action) => {
      const { auction, eventOrigin } = action.meta.arg;
      const { requestId } = action.meta;

      return {
        ...state,
        isOpen: true,
        isLoading: true,
        currentRequestId: requestId,
        eventOrigin: eventOrigin || state.eventOrigin,
        auction: {
          auction_id: auction.auction_id,
          auction_number: auction.auction_number,
          description: auction.description,
        },
      };
    });
    builder.addCase(fetchAuctionDetails.fulfilled, (state, action) => {
      const { details } = action.payload;
      const { requestId } = action.meta;

      if (state.currentRequestId !== requestId) {
        return state;
      }

      return {
        ...state,
        isLoading: false,
        currentRequestId: undefined,
        auction: {
          ...state.auction,
          ...details,
        },
      };
    });
    builder.addCase(fetchAuctionDetails.rejected, (state, action) => {
      return state;
    });
    builder.addMatcher(isFetchingAuctions, (state) => {
      return {
        ...state,
        isLoading: false,
        isOpen: false,
      };
    });
  },
});

export const auctionPreviewActions = {
  ...auctionPreviewSlice.actions,
  fetchAuctionDetails,
};

export default auctionPreviewSlice.reducer;
